import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

type Outcome = 'success' | 'error' | 'denied' | 'rate_limited';

interface Overview {
  settings: {
    apiEnabled: boolean;
    writesEnabled: boolean;
    pilotMode: 'all' | 'list';
    pilotEmails: string[];
    dailyChangeLimit: number;
    rateLimit: { windowMs: number; maxRequests: number };
  };
  totals: {
    providersWithAgents: number;
    activeConnections: number;
    requests7d: number;
    problems7d: number;
    changesConfirmed7d: number;
    feedbackTotal: number;
  };
  assistants: Array<{ assistant: string; activeConnections: number }>;
  providers: Array<{
    providerId: string;
    name: string;
    email: string;
    lastUsedAt: string | null;
    last7d: { requests: number; problems: number; changesConfirmed: number };
    connections: Array<{
      id: string;
      name: string;
      assistant: string | null;
      keyPrefix: string;
      scopes: string[];
      createdAt: string;
      lastUsedAt: string | null;
      revokedAt: string | null;
      termsAcceptedAt: string | null;
      termsVersion: string | null;
    }>;
  }>;
}

interface ActivityEntry {
  id: string;
  providerId: string;
  providerName: string;
  connectionName: string;
  assistant: string | null;
  action: string;
  outcome: Outcome;
  statusCode: number | null;
  summary: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
}

interface FeedbackEntry {
  id: string;
  providerName: string;
  providerEmail: string;
  kind: 'worked' | 'problem' | 'idea';
  assistant: string;
  message: string;
  createdAt: string;
}

const SCOPE_LABELS: Record<string, string> = {
  'schedule:read': 'Schedule',
  'customers:read': 'Customers',
  'financials:read': 'Financials',
  'schedule:write': 'Change schedule',
  'customers:write': 'Add customers',
};

const OUTCOME_CHIPS: Record<Outcome, { label: string; color: 'success' | 'error' | 'warning' }> = {
  success: { label: 'OK', color: 'success' },
  error: { label: 'Failed', color: 'error' },
  denied: { label: 'Not allowed', color: 'warning' },
  rate_limited: { label: 'Rate limited', color: 'warning' },
};

const FEEDBACK_CHIPS: Record<FeedbackEntry['kind'], { label: string; color: 'success' | 'error' | 'info' }> = {
  worked: { label: 'Worked well', color: 'success' },
  problem: { label: 'Problem', color: 'error' },
  idea: { label: 'Idea', color: 'info' },
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const Stat: React.FC<{ label: string; value: React.ReactNode; tone?: 'error' }> = ({ label, value, tone }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: tone === 'error' ? 'error.main' : undefined }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AdminAgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'providers' | 'activity' | 'feedback'>('providers');
  const [overview, setOverview] = useState<Overview | null>(null);
  const [activityFilter, setActivityFilter] = useState<'problems' | 'changes' | 'all'>('problems');
  const [activity, setActivity] = useState<ActivityEntry[] | null>(null);
  const [feedback, setFeedback] = useState<FeedbackEntry[] | null>(null);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    try {
      setError('');
      const res = await apiService.get<Overview>('/agent-admin/overview');
      setOverview(res.data as Overview);
    } catch (e: any) {
      setError(e?.message || 'Could not load the agent overview.');
    }
  }, []);

  const loadActivity = useCallback(async (filter: string) => {
    setActivity(null);
    try {
      const res = await apiService.get<ActivityEntry[]>(`/agent-admin/activity?filter=${filter}&limit=150`);
      setActivity((res.data as ActivityEntry[]) || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load agent activity.');
      setActivity([]);
    }
  }, []);

  const loadFeedback = useCallback(async () => {
    setFeedback(null);
    try {
      const res = await apiService.get<FeedbackEntry[]>('/agent-admin/feedback');
      setFeedback((res.data as FeedbackEntry[]) || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load feedback.');
      setFeedback([]);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (tab === 'activity') loadActivity(activityFilter);
    if (tab === 'feedback') loadFeedback();
  }, [tab, activityFilter, loadActivity, loadFeedback]);

  const refresh = () => {
    loadOverview();
    if (tab === 'activity') loadActivity(activityFilter);
    if (tab === 'feedback') loadFeedback();
  };

  const s = overview?.settings;
  const t = overview?.totals;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        Back to Admin
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          AI Agents
        </Typography>
        <Chip label="Pilot" color="warning" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<Refresh />} onClick={refresh}>
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Providers’ connected AI assistants: who is connected, what their agents are doing, what failed, and what providers
        are telling us. Read-only — switches live in Railway variables.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!overview ? (
        !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                size="small"
                color={s!.apiEnabled ? 'success' : 'default'}
                label={`Agent API: ${s!.apiEnabled ? 'on' : 'off'} (AGENT_API_ENABLED)`}
              />
              <Chip
                size="small"
                color={s!.writesEnabled ? 'warning' : 'default'}
                label={`Changes: ${s!.writesEnabled ? 'on' : 'off'} (AGENT_WRITES_ENABLED)`}
              />
              <Chip
                size="small"
                color={s!.pilotMode === 'all' ? 'info' : 'default'}
                label={
                  s!.pilotMode === 'all'
                    ? 'Who: all providers (AGENT_API_PROVIDER_EMAILS=*)'
                    : `Who: ${s!.pilotEmails.length} listed provider${s!.pilotEmails.length === 1 ? '' : 's'}`
                }
              />
              <Chip size="small" variant="outlined" label={`${s!.dailyChangeLimit} changes / agent / 24h`} />
              <Chip
                size="small"
                variant="outlined"
                label={`${s!.rateLimit.maxRequests} requests / ${Math.round(s!.rateLimit.windowMs / 60000)} min`}
              />
            </Stack>
            {s!.pilotMode === 'list' && s!.pilotEmails.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Pilot list: {s!.pilotEmails.join(', ')}
              </Typography>
            )}
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Stat label="Providers with agents" value={t!.providersWithAgents} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Stat label="Active agents" value={t!.activeConnections} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Stat label="Requests (7 days)" value={t!.requests7d} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Stat label="Problems (7 days)" value={t!.problems7d} tone={t!.problems7d > 0 ? 'error' : undefined} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Stat label="Changes made (7 days)" value={t!.changesConfirmed7d} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Stat label="Feedback" value={t!.feedbackTotal} />
            </Grid>
          </Grid>

          {overview.assistants.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Active agents by assistant:
              </Typography>
              {overview.assistants.map((a) => (
                <Chip
                  key={a.assistant}
                  size="small"
                  variant="outlined"
                  color={a.assistant === 'Not set' ? 'warning' : 'default'}
                  label={`${a.assistant} · ${a.activeConnections}`}
                />
              ))}
            </Stack>
          )}
        </>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="providers" label="Providers" />
        <Tab value="activity" label="Activity" />
        <Tab value="feedback" label="Feedback" />
      </Tabs>

      {tab === 'providers' && overview && (
        overview.providers.length === 0 ? (
          <Alert severity="info">No provider has connected an agent yet.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Agents</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell align="right">Requests (7d)</TableCell>
                  <TableCell align="right">Problems (7d)</TableCell>
                  <TableCell align="right">Changes (7d)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.providers.map((p) => (
                  <TableRow key={p.providerId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75}>
                        {p.connections.map((c) => (
                          <Box key={c.id}>
                            <Typography variant="body2" sx={{ textDecoration: c.revokedAt ? 'line-through' : undefined }}>
                              <strong>{c.assistant || 'Assistant not set'}</strong> · {c.name}{' '}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {c.keyPrefix}…
                              </Typography>
                              {c.revokedAt && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}
                                  revoked {formatDateTime(c.revokedAt)}
                                </Typography>
                              )}
                            </Typography>
                            {!c.revokedAt && (
                              <Typography
                                variant="caption"
                                color={c.termsAcceptedAt ? 'text.secondary' : 'warning.main'}
                                sx={{ display: 'block' }}
                              >
                                {c.termsAcceptedAt
                                  ? `Agent terms v${c.termsVersion} accepted ${formatDateTime(c.termsAcceptedAt)}`
                                  : 'Agent terms not accepted yet'}
                              </Typography>
                            )}
                            {!c.revokedAt && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                                {c.scopes.map((sc) => (
                                  <Chip
                                    key={sc}
                                    size="small"
                                    variant="outlined"
                                    color={sc.endsWith(':write') ? 'warning' : 'default'}
                                    label={SCOPE_LABELS[sc] || sc}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDateTime(p.lastUsedAt)}</TableCell>
                    <TableCell align="right">{p.last7d.requests}</TableCell>
                    <TableCell align="right" sx={{ color: p.last7d.problems > 0 ? 'error.main' : undefined }}>
                      {p.last7d.problems}
                    </TableCell>
                    <TableCell align="right">{p.last7d.changesConfirmed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {tab === 'activity' && (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={activityFilter}
            onChange={(_, v) => v && setActivityFilter(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="problems">Problems only</ToggleButton>
            <ToggleButton value="changes">Changes</ToggleButton>
            <ToggleButton value="all">Everything</ToggleButton>
          </ToggleButtonGroup>
          {activity === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : activity.length === 0 ? (
            <Alert severity="info">Nothing here yet.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Provider · agent</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activity.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{e.providerName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {e.assistant ? `${e.assistant} · ` : ''}
                          {e.connectionName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{e.action}</TableCell>
                      <TableCell>
                        {e.summary && <Typography variant="body2">{e.summary}</Typography>}
                        {e.outcome !== 'success' && e.errorMessage && (
                          <Typography variant="caption" color="error">
                            {e.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={OUTCOME_CHIPS[e.outcome]?.color}
                          label={`${OUTCOME_CHIPS[e.outcome]?.label || e.outcome}${e.statusCode ? ` · ${e.statusCode}` : ''}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 'feedback' &&
        (feedback === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : feedback.length === 0 ? (
          <Alert severity="info">No feedback yet. Providers send it from Settings → Connected AI Agents → Send feedback.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {feedback.map((f) => (
              <Paper key={f.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Chip size="small" color={FEEDBACK_CHIPS[f.kind].color} label={FEEDBACK_CHIPS[f.kind].label} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {f.providerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <a href={`mailto:${f.providerEmail}`}>{f.providerEmail}</a>
                    {f.assistant ? ` · ${f.assistant}` : ''} · {formatDateTime(f.createdAt)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {f.message}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ))}
    </Container>
  );
};

export default AdminAgentsPage;
